/** Matrix navigation: a few global destinations and the tabs of one project. */

import { FORGOT_PATH, RESET_PATH, VERIFY_PATH } from './public-paths.js';

export type NavItem = {
  href: string;
  label: string;
  projected: boolean;
  icon: NavIcon;
  sidebar: boolean;
};

export type NavIcon =
  | 'home'
  | 'projects'
  | 'changes'
  | 'agents'
  | 'explore'
  | 'activity'
  | 'evidence'
  | 'context'
  | 'crucible'
  | 'settings'
  | 'intent'
  | 'inspect'
  | 'oracle'
  | 'compose'
  | 'runtime'
  | 'project'
  | 'policy'
  | 'frontier'
  | 'search'
  | 'chain'
  | 'graph'
  | 'causal'
  | 'wave'
  | 'flow'
  | 'history'
  | 'certify'
  | 'pressure'
  | 'repair'
  | 'code';

export const PRODUCT_HOME_PATH = '/home';
export const PROJECTS_PATH = '/projects';
export const SETTINGS_PATH = '/settings';
export const PROFILE_PATH = '/profile';
export const REPLAY_PATH = '/replay';

/** Global destinations. Everything about one project lives under `/p/<project>`. */
export const NAV: NavItem[] = [
  { href: PRODUCT_HOME_PATH, label: 'Home', projected: true, icon: 'home', sidebar: true },
  { href: PROJECTS_PATH, label: 'Projects', projected: true, icon: 'projects', sidebar: true },
  /* Account settings live behind the header's gear and the avatar, as on GitHub; the sidebar's Settings is the project's. */
  { href: SETTINGS_PATH, label: 'Account settings', projected: true, icon: 'settings', sidebar: false },
  { href: PROFILE_PATH, label: 'Developer Profile', projected: true, icon: 'agents', sidebar: false },
  { href: REPLAY_PATH, label: 'Replay receipt', projected: true, icon: 'evidence', sidebar: false },
];

export type ProjectTab = 'overview' | 'code' | 'changes' | 'work' | 'checks' | 'insights' | 'settings';

export type ProjectTabItem = { tab: ProjectTab; label: string; icon: NavIcon };

/** The tabs of a project, in the order the project frame and the sidebar draw them. */
export const PROJECT_TABS: readonly ProjectTabItem[] = [
  { tab: 'overview', label: 'Overview', icon: 'project' },
  { tab: 'code', label: 'Code', icon: 'code' },
  { tab: 'changes', label: 'Changes', icon: 'changes' },
  { tab: 'work', label: 'Work', icon: 'flow' },
  { tab: 'checks', label: 'Checks', icon: 'certify' },
  { tab: 'insights', label: 'Insights', icon: 'graph' },
  { tab: 'settings', label: 'Settings', icon: 'policy' },
];

/**
 * The project the shell is showing: the one a `/p/<project>/…` address names,
 * else the one app.ts reads (`project-directory.ts#viewedProject`). Every
 * project link is built against it; with none chosen yet, links go to
 * Projects.
 */
let activeProject: string | null = null;

export function setCurrentProject(project: string | null): void {
  activeProject = project;
}

export function currentProject(): string | null {
  return activeProject;
}

const PROJECT_PREFIX = '/p/';

/** `/p/<project>/<tab>?<query>`; overview is the project root. */
export function projectHref(
  tab: ProjectTab,
  query: Readonly<Record<string, string>> = {},
  project: string | null = activeProject,
): string {
  if (project === null) return PROJECTS_PATH;
  const base = `${PROJECT_PREFIX}${encodeURIComponent(project)}`;
  const path = tab === 'overview' ? base : `${base}/${tab}`;
  const search = new URLSearchParams(query).toString();
  return search.length > 0 ? `${path}?${search}` : path;
}

export type ProjectRoute = {
  project: string;
  tab: ProjectTab;
  /** `/changes/<id>`: one change. */
  changeId: string | null;
  /** `/code/history`. */
  sub: 'history' | null;
};

const TAB_IDS = new Set<string>(PROJECT_TABS.map((item) => item.tab));

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * The project route an address names, or null when it names none.
 *
 * A project is `owner/repo`. The shell writes it as one encoded segment
 * (`/p/owner%2Frepo/code`), but the address a person types or pastes is the
 * GitHub shape, `/p/owner/repo/code`, and a reload of either must land on the
 * same page. The project is every segment before the first tab name, at most
 * two; a bare `/p/owner/repo` is that project's overview, and `overview` is
 * also accepted as a tab name. Tab names are therefore reserved as the second
 * segment of a raw project address.
 */
export function parseProjectPath(path: string): ProjectRoute | null {
  if (!path.startsWith(PROJECT_PREFIX)) return null;
  const segments = path.slice(PROJECT_PREFIX.length).split('/');
  if (segments.length > 1 && segments[segments.length - 1] === '') segments.pop();
  const tabAt = segments.findIndex((segment, index) => index > 0 && (segment === 'overview' || TAB_IDS.has(segment)));
  const projectEnd = tabAt === -1 ? Math.min(segments.length, 2) : tabAt;
  if (projectEnd > 2 || (tabAt === -1 && segments.length > 2)) return null;
  const owner = segments.slice(0, projectEnd);
  if (owner.some((segment) => segment === '')) return null;
  const base: ProjectRoute = { project: owner.map(decodeSegment).join('/'), tab: 'overview', changeId: null, sub: null };
  if (tabAt === -1) return base;
  const [tab, detail, ...rest] = segments.slice(tabAt);
  if (rest.length > 0 || tab === undefined) return null;
  if (tab === 'overview') return detail === undefined ? base : null;
  const route: ProjectRoute = { ...base, tab: tab as ProjectTab };
  if (detail === undefined) return route;
  // A change is only ever read here; `new` names nothing.
  if (tab === 'changes' && detail === 'new') return null;
  if (tab === 'changes' && detail !== '') return { ...route, changeId: decodeSegment(detail) };
  if (tab === 'code' && detail === 'history') return { ...route, sub: 'history' };
  return null;
}

/**
 * Whether an address a person can type is one a person can also click to.
 *
 * `arrival` is a route reached from outside the product — a link in an email
 * — so nothing inside it can lead there and nothing should. It is the one
 * permanent exemption.
 *
 * The other three name the *shape* of the gap, and they are shapes the walk
 * measures rather than words this file chooses. `orphan` is a route no
 * rendered surface links at all. `cluster` is a route that is linked, but only
 * from surfaces that cannot themselves be reached. `conditional` is a route
 * whose only inbound link is drawn by one answer of an address that has
 * several — present when the authority says one thing, gone when it says
 * another.
 *
 * The distinction is what a reader can do, not what the markup contains: a
 * repair for an orphan is a link from somewhere, and a repair for a cluster is
 * one door into the cluster. Guessing wrong between them is how this list came
 * to claim that one entry made all twelve lenses reachable when two of them
 * were linked by nothing.
 */
export type UnlinkedKind = 'arrival' | 'orphan' | 'cluster' | 'conditional';

export type UnlinkedRoute = {
  href: string;
  kind: UnlinkedKind;
  reason: string;
};

/**
 * Every addressable route no rendered link leads to.
 *
 * Measured, not asserted: `ui/tests/reachability-routes.test.ts` renders every
 * surface — every arm of every address that has more than one — walks the
 * hrefs from `/`, and fails when a route is missing from this list, when a
 * route on it has become reachable, and when the `kind` an entry claims is not
 * the shape the walk measured. The sidebar and the project tabs link every
 * product route, so only the arrivals from email remain.
 */
export const UNLINKED_ROUTES: readonly UnlinkedRoute[] = [
  {
    href: RESET_PATH,
    kind: 'arrival',
    reason:
      'Opened from the reset link in an email. The one-time token lives in the address, so a link from inside the product would carry none.',
  },
  {
    href: VERIFY_PATH,
    kind: 'arrival',
    reason:
      'Opened from the verification link in an email, or reached by the sign-up redirect. A link from inside the product would spend nothing.',
  },
  {
    href: FORGOT_PATH,
    kind: 'conditional',
    reason:
      'Linked from sign-in only where `/auth/session` reports that this deployment can mail a reset link. Without mail the Worker refuses a reset request, so that sign-in offers no link a person could not finish.',
  },
];

/** The certified state history of the current project. */
export function codeHistoryHref(): string {
  return activeProject === null ? PROJECTS_PATH : `${projectHref('code')}/history`;
}

/** One change of the current project, optionally on one of its views. */
export function changeDetailHref(id: string, view?: string): string {
  const changes = projectHref('changes');
  const href = activeProject === null ? changes : `${changes}/${encodeURIComponent(id)}`;
  return view ? `${href}?view=${encodeURIComponent(view)}` : href;
}

/** Read-only replay address: the digest is the only selectable receipt input. */
export function replayDetailHref(digest: string): string {
  return `${REPLAY_PATH}?digest=${encodeURIComponent(digest)}`;
}

/** The change an address names, or null. */
export function changeIdFromPath(path: string): string | null {
  return parseProjectPath(path)?.changeId ?? null;
}

export function isChangeDetailPath(path: string): boolean {
  return changeIdFromPath(path) !== null;
}

/** The first allowed value of `name` in `search`, or `fallback`. */
export function requestedView<Id extends string>(
  search: string,
  name: string,
  allowed: readonly Id[],
  fallback: Id,
): Id {
  const value = new URLSearchParams(search).get(name);
  return allowed.find((id) => id === value) ?? fallback;
}

/** A primary, unmodified click: the only one a client router takes over from the browser. */
export function shouldInterceptNavigation(
  event: Pick<MouseEvent, 'button' | 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'>,
): boolean {
  return event.button === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
}

/** The Context view of one stored change. */
export function contextHref(id: string): string {
  return changeDetailHref(id, 'context');
}

/** 16×16 marks matching the consumer product sidebar. */
export function navGlyph(icon: NavIcon): string {
  const paths: Record<NavIcon, string> = {
    home: 'M2 8 8 2l6 6M3.5 7.2V14h3.2v-4h2.6v4H12.5V7.2',
    projects: 'M2 3h5l1.5 2H14v8H2zM4 7h8',
    changes: 'M3 4h8l2 2-2 2H3M13 12H5l-2-2 2-2',
    agents: 'M5 6a3 3 0 106 0 3 3 0 00-6 0M2 14c.5-3 2.5-4.5 6-4.5s5.5 1.5 6 4.5',
    explore: 'M7 2a5 5 0 105 5 5 5 0 00-5-5M11 11l3 3',
    activity: 'M2 4h7M2 8h11M2 12h8M12 3v2M12 11v2',
    evidence: 'M4 2h7l2 2v10H4zM6 7h5M6 10h5',
    context: 'M3 4h3v3H3zM10 3h3v3h-3zM9 10h4v3H9zM6 5h4M5 7v4h4',
    crucible: 'M5 2h6v4l3 8H2l3-8zM5 10h6',
    settings: 'M8 5.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5zM8 1.5l.7 1.6 1.7-.4 1.1 1.3-.4 1.7 1.6.8-.8 1.5 1.6.8-.4 1.7-1.3 1.1.4 1.7L8 14.5l-.7-1.6-1.7.4-1.1-1.3.4-1.7L3.3 9.5l.8-1.5L2.5 7.2l.4-1.7 1.3-1.1-.4-1.7L5.5 2.7 6.3 1.1 8 1.5z',
    intent: 'M8 2v12M2 8h12',
    inspect: 'M6 6h8v8H6zM2 2h4v4H2z',
    oracle: 'M8 2a6 6 0 100 12A6 6 0 008 2zM8 5v3l2 2',
    compose: 'M3 13l8-8 2 2-8 8H3z',
    runtime: 'M2 8h12M8 2v12',
    project: 'M2 12l6-8 6 8H2z',
    policy: 'M8 2l6 3v4c0 3-2.5 5-6 6C4.5 14 2 12 2 9V5z',
    frontier: 'M2 14L8 2l6 12',
    search: 'M7 7a3 3 0 106 0 3 3 0 10-6 0M11 11l3 3',
    chain: 'M6 10a3 3 0 010-4l2-2a3 3 0 014 4M10 6a3 3 0 010 4l-2 2a3 3 0 01-4-4',
    graph: 'M3 3h3v3H3zM10 3h3v3h-3zM6.5 10h3v3h-3zM6 4.5h4M5 6l2.5 4M11 6l-2.5 4',
    causal: 'M2 8h3l2-4 2 8 2-4h3',
    wave: 'M2 12c3-6 9-6 12 0M8 3v9',
    flow: 'M2 4h5M2 8h8M2 12h11M11 2l2 2-2 2',
    history: 'M3.5 4.5A5 5 0 118 13M3 2v3h3M8 5v3l2 1.5',
    certify: 'M4 2h8v7l-4 3-4-3zM6 6l1.5 1.5L10 5',
    pressure: 'M2 13h12M4 13V9M7 13V5M10 13V7M13 13V3',
    repair: 'M10.5 2a3 3 0 00-3 4L2 11.5 4.5 14 10 8.5a3 3 0 004-3l-2 1-1.5-1.5 1-2z',
    code: 'M5.5 4.5 2 8l3.5 3.5M10.5 4.5 14 8l-3.5 3.5M9 3 7 13',
  };
  return paths[icon];
}
